<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 py-4 px-6">
                        <div class="flex items-center gap-4">
                                <button
                                        type="button"
                                        class="flex items-center justify-center w-10 h-10 bg-transparent border border-gray-200 rounded-lg cursor-pointer text-gray-500 hover:bg-gray-50"
                                        @click="goBack"
                                >
                                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
                                        </svg>
                                </button>
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">
                                                {{ loading ? 'Loading...' : 'Edit Sticker' }}
                                        </h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">Sticker ID: {{ routeId }}</p>
                                </div>
                        </div>
                </header>

                <!-- Loading State -->
                <div v-if="loading" class="flex justify-center items-center py-16 px-4">
                        <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span class="ml-3 text-gray-500 text-sm">Loading sticker...</span>
                </div>

                <!-- Main Content -->
                <main v-else class="p-6">
                        <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                <div class="p-6">
                                        <h3 class="text-base font-semibold text-gray-900 m-0 mb-4">Sticker Properties</h3>
                                        <div class="max-w-lg">
                                                <!-- Image Section -->
                                                <div class="mb-6">
                                                        <label class="block text-sm font-medium text-gray-700 mb-2">Sticker Image (SVG only)</label>
                                                        <input
                                                                ref="fileInputRef"
                                                                type="file"
                                                                accept=".svg,image/svg+xml"
                                                                class="hidden"
                                                                @change="handleFileSelect"
                                                        >
                                                        <div class="flex items-start gap-4">
                                                                <div class="w-32 h-32 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0">
                                                                        <img
                                                                                v-if="sticker.url"
                                                                                :src="sticker.url"
                                                                                alt="Sticker image"
                                                                                class="w-full h-full object-contain"
                                                                        >
                                                                        <svg v-else class="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                        </svg>
                                                                </div>
                                                                <div class="flex flex-col gap-2">
                                                                        <button
                                                                                type="button"
                                                                                class="inline-flex items-center gap-2 py-2 px-4 bg-orange-7 text-white border-none rounded-lg text-sm cursor-pointer hover:bg-orange-6 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                :disabled="isUploadingFile"
                                                                                @click="uploadImage"
                                                                        >
                                                                                <svg v-if="!isUploadingFile" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                                                                </svg>
                                                                                <svg v-else class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                                                                </svg>
                                                                                {{ isUploadingFile ? 'Uploading...' : 'Upload SVG' }}
                                                                        </button>
                                                                        <button
                                                                                v-if="sticker.url"
                                                                                type="button"
                                                                                class="py-2 px-4 text-sm text-red-600 bg-transparent border border-red-200 rounded-lg cursor-pointer hover:bg-red-50"
                                                                                @click="removeImage"
                                                                        >
                                                                                Remove Image
                                                                        </button>
                                                                </div>
                                                        </div>
                                                </div>

                                                <!-- Save Button -->
                                                <div class="pt-4 border-t border-gray-200">
                                                        <button
                                                                type="button"
                                                                :disabled="saving"
                                                                class="py-2.5 px-6 bg-orange-7 hover:bg-orange-6 text-white border-none rounded-lg text-sm cursor-pointer disabled:opacity-50"
                                                                @click="saveSticker"
                                                        >
                                                                {{ saving ? 'Saving...' : 'Save Changes' }}
                                                        </button>
                                                </div>
                                        </div>
                                </div>
                        </div>
                </main>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { StickerModel } from '@sosocio/creator/interfaces';
import StickerService from 'services/StickerService';
import { httpClient } from 'utils/http';
import Swal from 'sweetalert2';

export default defineComponent({
        name: 'EditStickerPage',
        setup() {
                const route = useRoute();
                const router = useRouter();
                
                const routeId = computed(() => route.params.id as string);
                const loading = ref(true);
                const saving = ref(false);
                const fileInputRef = ref<HTMLInputElement | null>(null);
                const isUploadingFile = ref(false);
                
                const sticker = ref<Partial<StickerModel>>({
                        id: 0,
                        url: '',
                });
                
                const fetchSticker = async () => {
                        loading.value = true;
                        try {
                                const id = parseInt(routeId.value);
                                const data = await StickerService.get(id);
                                sticker.value = { ...data };
                        } catch (error) {
                                console.error('Error fetching sticker:', error);
                                await Swal.fire({
                                        title: 'Error',
                                        text: 'Failed to load sticker',
                                        icon: 'error',
                                });
                                router.push('/sticker');
                        } finally {
                                loading.value = false;
                        }
                };
                
                const goBack = () => {
                        router.push('/sticker');
                };
                
                const uploadImage = () => {
                        fileInputRef.value?.click();
                };

                const handleFileSelect = async (event: Event) => {
                        const target = event.target as HTMLInputElement;
                        const file = target.files?.[0];
                        if (!file) return;

                        if (!file.type.includes('svg') && !file.name.endsWith('.svg')) {
                                Swal.fire({
                                        title: 'Invalid File',
                                        text: 'Please select an SVG file',
                                        icon: 'error',
                                });
                                target.value = '';
                                return;
                        }

                        isUploadingFile.value = true;
                        try {
                                const formData = new FormData();
                                formData.append('file', file);

                                const response = await httpClient.post('/api/upload', formData, {
                                        headers: {
                                                'Content-Type': 'multipart/form-data',
                                        },
                                });

                                if (response.data?.url) {
                                        sticker.value.url = response.data.url;
                                } else if (typeof response.data === 'string') {
                                        sticker.value.url = response.data;
                                } else {
                                        Swal.fire({
                                                title: 'Error',
                                                text: 'Upload succeeded but no URL returned',
                                                icon: 'error',
                                        });
                                }
                        } catch (error: any) {
                                Swal.fire({
                                        title: 'Upload Failed',
                                        text: error.response?.data?.message || error.message || 'Failed to upload file',
                                        icon: 'error',
                                });
                        } finally {
                                isUploadingFile.value = false;
                                target.value = '';
                        }
                };
                
                const removeImage = () => {
                        sticker.value.url = '';
                };
                
                const saveSticker = async () => {
                        if (!sticker.value.url?.trim()) {
                                await Swal.fire({
                                        title: 'Validation Error',
                                        text: 'Please provide an image URL for the sticker',
                                        icon: 'warning',
                                });
                                return;
                        }
                        
                        saving.value = true;
                        try {
                                await StickerService.update(sticker.value as StickerModel);
                                Swal.fire({
                                        toast: true,
                                        position: 'top-end',
                                        icon: 'success',
                                        title: 'Sticker saved successfully',
                                        showConfirmButton: false,
                                        timer: 3000,
                                });
                        } catch (error: any) {
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to save sticker',
                                        icon: 'error',
                                });
                        } finally {
                                saving.value = false;
                        }
                };
                
                onMounted(() => {
                        fetchSticker();
                });
                
                return {
                        routeId,
                        loading,
                        saving,
                        sticker,
                        goBack,
                        uploadImage,
                        handleFileSelect,
                        fileInputRef,
                        isUploadingFile,
                        removeImage,
                        saveSticker,
                };
        },
});
</script>
